<script lang="ts" setup>
defineProps<{
  config: any
  skeleton: boolean
  isEmailLogin: number
  availableLogins: string[]
  showLogout: string | null
  form: any
  userAgreementUrl: string
  privacyPolicyUrl: string
  loginBgUrl: string
  signInWithFacebook: () => void
  signInWithGoogle: () => void
  signInWithApple: () => void
  logout: () => void
  setEmailLogin: () => void
  getCode: () => void
  beforeSignIn: () => void
  back: () => void
}>()

const emit = defineEmits<{
  (e: 'update-form-field', field: 'email' | 'code', value: string): void
}>()

function updateFormField(field: 'email' | 'code', event: Event) {
  emit('update-form-field', field, (event.target as HTMLInputElement).value)
}
</script>

<template>
  <div class="login-page">
    <div v-if="!skeleton" class="login-bg">
      <div class="fixed top-32px left-12px" @click="back()">
        <i-icon-park-outline-left class="w-6 h-6 color-n-text-2" />
      </div>

      <div v-if="!isEmailLogin" class="mt-180px flex flex-col items-center font-medium">
        <img :src="config.resources.logo" width="80">
        <img :src="config.resources.novel" width="108" class="mt-4">
        <div class="mt-12" />
        <div
          v-if="availableLogins.includes('facebook')"
          class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center facebook-btn login-third-btn"
          @click="signInWithFacebook"
        >
          <img :src="config.resources.facebookIcon" width="20" class="absolute top-50% mt--10px left-30px">
          <span class="login-third-btn__text">{{ config.texts.signInWithFacebook }}</span>
        </div>
        <div
          v-if="availableLogins.includes('google')"
          class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center google-btn login-third-btn"
          @click="signInWithGoogle"
        >
          <img :src="config.resources.googleIcon" width="20" class="absolute top-50% mt--10px left-30px">
          <span class="login-third-btn__text">{{ config.texts.signInWithGoogle }}</span>
        </div>
        <div
          v-if="availableLogins.includes('apple')"
          class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center apple-btn login-third-btn"
          @click="signInWithApple"
        >
          <img :src="config.resources.appleIcon" width="20" class="absolute top-50% mt--10px left-30px">
          <span class="login-third-btn__text">{{ config.texts.signInWithApple }}</span>
        </div>

        <div v-if="showLogout === '1'" class="mt-4 relative w-300px h-45px border-rd-25px text-n-text-inverse flex items-center justify-center google-btn" @click="logout">
          {{ config.texts.logout }}
        </div>
        <div class="mt-30px text-n-primary text-center" @click="setEmailLogin">
          {{ config.texts.continueWithEmail }}
        </div>
      </div>

      <div v-else class="mt-180px flex flex-col items-center">
        <div class="text-20px font-bold text-n-text-1">
          {{ config.texts.signInWithEmail }}
        </div>

        <div class="w-300px">
          <div class="text-n-text-3 text-12px mt-10px mb-5px">
            {{ config.texts.yourEmail }}
          </div>
          <div
            class="flex items-center space-x-5 relative border border-1px rounded-full overflow-hidden"
            :style="{ borderColor: config.theme.inputBorder }"
          >
            <img class="w-4 h-4 absolute left-4 top-4" :src="config.resources.emailIcon">
            <input
              :value="form.email"
              class="!ml0 flex-1 h-11 w-full pl-10 pr-4 rounded-full border-0 !outline-0 !bg-transparent text-n-text-1"
              placeholder=""
              maxlength="50"
              @input="updateFormField('email', $event)"
            >
          </div>
        </div>

        <div class="w-300px">
          <div class="text-n-text-3 text-12px mt-10px mb-5px">
            {{ config.texts.verificationCode }}
          </div>
          <div
            class="flex items-center space-x-5 relative border border-1px rounded-full overflow-hidden"
            :style="{ borderColor: config.theme.inputBorder }"
          >
            <img class="w-4 h-4 absolute left-4 top-4" :src="config.resources.emailCodeIcon">
            <input
              :value="form.code"
              class="!ml0 flex-1 h-11 w-full pl-10 pr-4 rounded-full border-0 !outline-0 !bg-transparent text-n-text-1"
              placeholder=""
              maxlength="6"
              @input="updateFormField('code', $event)"
            >
            <div v-if="form.timeout > 0" class="text-n-text-3 text-align-left pr-3">
              {{ form.timeout }}s
            </div>
            <div
              v-else
              class=" text-n-primary text-12px text-align-left pr-3"
              @click="getCode"
            >
              {{ config.texts.getVerificationCode }}
            </div>
          </div>
        </div>

        <div class="w-300px h-45px text-n-text-inverse btn-solid line-height-45px text-center border-rd-23px mt-30px" @click="beforeSignIn">
          {{ config.texts.signIn }}
        </div>
      </div>

      <div class="fixed bottom-48px w-282px text-n-text-3 text-12px text-center">
        {{ config.texts.userAgreement }} <a :href="userAgreementUrl">{{ config.texts.userAgreementLink }}</a> {{ config.texts.and }} <a :href="privacyPolicyUrl">{{ config.texts.privacyPolicyLink }}</a>
      </div>
    </div>

    <div v-else class="h-100dvh">
      <div class="pt-20vh flex justify-center">
        <BasicSpinner class="mx-auto" />
      </div>
      <p class="mt-4 text-center font-bold">
        {{ config.texts.loading }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.login-bg {
  width: 100vw;
  height: 100vh;
  background-image: v-bind(loginBgUrl);
  background-size: 100% 100%;
  display: flex;
  justify-content: center;
  position: relative;

  a {
    text-decoration: underline;
  }
}

.login-third-btn {
  padding-left: 56px;
  padding-right: 16px;
  box-sizing: border-box;
}

.login-third-btn__text {
  display: block;
  width: 100%;
  min-width: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.facebook-btn {
  background-color: v-bind('config.theme.facebookBlue');
}

.google-btn {
  background-color: v-bind('config.theme.googleBlue');
}

.apple-btn {
  background-color: v-bind('config.theme.appleDark');
}
</style>
